import React, { useState, useEffect } from 'react';
import { leadsAPI, usersAPI } from '../../services/api';

function ManagerDashboard() {
  const [stats, setStats] = useState({
    totalLeads: 0,
    totalUsers: 0,
    byStatus: [],
    bySource: [],
    userAssignments: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [leadsStatsResponse, usersResponse, leadsResponse] = await Promise.all([
        leadsAPI.getLeadStats(),
        usersAPI.getAllUsers({ limit: 100 }),
        leadsAPI.getAllLeads({ limit: 1000 })
      ]);
      
      const users = usersResponse.data.users || usersResponse.data;
      const leads = leadsResponse.data.leads || leadsResponse.data;
      
      // Calculate user assignments
      const userAssignments = users.map(user => {
        const leadCount = leads.filter(lead => 
          lead.primaryAssignee?._id === user._id || 
          lead.secondaryAssignees?.some(assignee => assignee._id === user._id)
        ).length || 0;
        return { _id: user._id, username: user.username, leadCount };
      }).sort((a, b) => b.leadCount - a.leadCount);
      
      setStats({
        ...leadsStatsResponse.data,
        totalUsers: users.length,
        userAssignments
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed Won': return 'bg-green-100 text-green-800';
      case 'Closed Lost': return 'bg-red-100 text-red-800';
      case 'Cold Lead': return 'bg-gray-100 text-gray-800';
      case 'Prospecting': return 'bg-blue-100 text-blue-800';
      case 'Qualified': return 'bg-emerald-100 text-emerald-800';
      case 'Demo Scheduled / Done': return 'bg-purple-100 text-purple-800';
      case 'Evaluation / Pilot': return 'bg-indigo-100 text-indigo-800';
      case 'Decision': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-xl border border-gray-200/50">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center space-x-3 sm:space-x-4 mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-blue-600 bg-clip-text text-transparent">
                  Manager Dashboard
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mt-1">
                  Welcome back! Here's an overview of your CRM system.
                </p>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm sm:text-base">{error}</span>
            </div>
          )}

          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 sm:p-6 rounded-xl border border-blue-200 hover:shadow-xl hover:scale-105 transition-all duration-300 group cursor-pointer">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 rounded-full bg-blue-100 mr-3 sm:mr-4 flex-shrink-0 group-hover:bg-blue-200 transition-colors duration-300">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-blue-600 truncate group-hover:text-blue-700 transition-colors duration-300">Total Leads</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-900 group-hover:text-blue-800 transition-colors duration-300">{stats.totalLeads}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 sm:p-6 rounded-xl border border-green-200 hover:shadow-xl hover:scale-105 transition-all duration-300 group cursor-pointer">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 rounded-full bg-green-100 mr-3 sm:mr-4 flex-shrink-0 group-hover:bg-green-200 transition-colors duration-300">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 group-hover:text-green-700 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-green-600 truncate group-hover:text-green-700 transition-colors duration-300">Total Users</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-900 group-hover:text-green-800 transition-colors duration-300">{stats.totalUsers}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 sm:p-6 rounded-xl border border-yellow-200 hover:shadow-xl hover:scale-105 transition-all duration-300 group cursor-pointer">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 rounded-full bg-yellow-100 mr-3 sm:mr-4 flex-shrink-0 group-hover:bg-yellow-200 transition-colors duration-300">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 group-hover:text-yellow-700 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-yellow-600 truncate group-hover:text-yellow-700 transition-colors duration-300">Won Leads</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-900 group-hover:text-yellow-800 transition-colors duration-300">
                    {stats.byStatus.find(s => s._id === 'Closed Won')?.count || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 sm:p-6 rounded-xl border border-purple-200 hover:shadow-xl hover:scale-105 transition-all duration-300 group cursor-pointer">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 rounded-full bg-purple-100 mr-3 sm:mr-4 flex-shrink-0 group-hover:bg-purple-200 transition-colors duration-300">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 group-hover:text-purple-700 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-purple-600 truncate group-hover:text-purple-700 transition-colors duration-300">Conversion Rate</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-900 group-hover:text-purple-800 transition-colors duration-300">
                    {stats.totalLeads > 0 
                      ? Math.round(((stats.byStatus.find(s => s._id === 'Closed Won')?.count || 0) / stats.totalLeads) * 100)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                Lead Status Distribution
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {stats.byStatus.map((status) => (
                  <div key={status._id} className="flex items-center justify-between gap-3">
                    <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(status._id)} truncate flex-shrink-0`}>
                      {status._id}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 sm:h-2.5 mx-2 sm:mx-3">
                      <div 
                        className={`h-full rounded-full transition-all duration-300 ${getStatusColor(status._id).replace('100', '500').replace('text', 'bg')}`}
                        style={{ width: `${(status.count / stats.totalLeads) * 100}%` }}
                      ></div>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm sm:text-base flex-shrink-0">
                      {status.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
                Lead Source Distribution
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {stats.bySource.map((source) => (
                  <div key={source._id} className="flex items-center justify-between gap-3">
                    <span className="px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-indigo-100 text-indigo-800 truncate flex-shrink-0">
                      {source._id}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 sm:h-2.5 mx-2 sm:mx-3">
                      <div 
                        className="h-full rounded-full bg-indigo-500 transition-all duration-300"
                        style={{ width: `${(source.count / stats.totalLeads) * 100}%` }}
                      ></div>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm sm:text-base flex-shrink-0">
                      {source.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
                User Lead Assignments
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {stats.userAssignments?.map((user) => (
                  <div key={user._id} className="flex items-center justify-between gap-3">
                    <span className="px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-emerald-100 text-emerald-800 truncate flex-shrink-0">
                      {user.username}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 sm:h-2.5 mx-2 sm:mx-3">
                      <div 
                        className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                        style={{ width: `${stats.totalLeads > 0 ? (user.leadCount / stats.totalLeads) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm sm:text-base flex-shrink-0">
                      {user.leadCount}
                    </span>
                  </div>
                )) || (
                  <p className="text-gray-500 text-sm">No user assignments found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManagerDashboard;